import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Breadcrumbs,
  Link,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useNavigate } from 'react-router-dom';
import { ArrowBack, Save } from '@mui/icons-material';
import { projectsService, Project } from '../services/projectsService';

interface QualityControlFormData {
  title: string;
  description: string;
  project: string;
  inspector: string;
  inspectionType: string;
  location: string;
  scheduledDate: string;
  status: string;
  priority: string;
  checklist: string;
  notes: string;
}

const QualityControlCreate: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<QualityControlFormData>({
    title: '',
    description: '',
    project: '',
    inspector: '',
    inspectionType: '',
    location: '',
    scheduledDate: '',
    status: 'Scheduled',
    priority: 'Medium',
    checklist: '',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const resp = await projectsService.getAll();
        // Backend returns projects under response.data.data
        const data = (resp as any)?.data?.data || (resp as any)?.data?.projects || [];
        setProjects(Array.isArray(data) ? data : []);
      } catch (err) {
        console.warn('Failed to load projects for QC form', err);
      }
    };
    fetchProjects();
  }, []);

  const inspectionTypes = [
    'Foundation Inspection',
    'Framing Inspection',
    'Electrical Inspection',
    'Plumbing Inspection',
    'HVAC Inspection',
    'Insulation Inspection',
    'Drywall Inspection',
    'Final Inspection',
    'Safety Inspection',
    'Material Quality Check',
  ];

  const statuses = [
    'Scheduled',
    'In Progress',
    'Completed',
    'Failed',
    'Cancelled',
  ];

  const priorities = [
    'Low',
    'Medium',
    'High',
    'Critical',
  ];

  const handleInputChange = (field: keyof QualityControlFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSelectChange = (field: keyof QualityControlFormData) => (
    event: any
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/quality-control', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to create quality control inspection');
      }

      navigate('/quality-control');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/quality-control')}
          sx={{ textDecoration: 'none' }}
        >
          Quality Control
        </Link>
        <Typography color="text.primary">Create Inspection</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/quality-control')}
          sx={{ mr: 2 }}
        >
          Back to Quality Control
        </Button>
        <Typography variant="h4" component="h1">
          Create New Inspection
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Inspection Title"
                  value={formData.title}
                  onChange={handleInputChange('title')}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth required>
                  <InputLabel>Inspection Type</InputLabel>
                  <Select
                    value={formData.inspectionType}
                    onChange={handleSelectChange('inspectionType')}
                    label="Inspection Type"
                  >
                    {inspectionTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={handleInputChange('description')}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth required>
                  <InputLabel>Project</InputLabel>
                  <Select
                    value={formData.project}
                    onChange={handleSelectChange('project')}
                    label="Project"
                  >
                    {projects.map((p) => (
                      <MenuItem key={p._id} value={p._id}>{p.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Inspector"
                  value={formData.inspector}
                  onChange={handleInputChange('inspector')}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Location"
                  value={formData.location}
                  onChange={handleInputChange('location')}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Scheduled Date"
                  type="datetime-local"
                  value={formData.scheduledDate}
                  onChange={handleInputChange('scheduledDate')}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={handleSelectChange('status')}
                    label="Status"
                  >
                    {statuses.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={formData.priority}
                    onChange={handleSelectChange('priority')}
                    label="Priority"
                  >
                    {priorities.map((priority) => (
                      <MenuItem key={priority} value={priority}>
                        {priority}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Checklist Items"
                  value={formData.checklist}
                  onChange={handleInputChange('checklist')}
                  multiline
                  rows={4}
                  placeholder="Enter checklist items (one per line)"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Notes"
                  value={formData.notes}
                  onChange={handleInputChange('notes')}
                  multiline
                  rows={3}
                  placeholder="Additional notes or observations"
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<Save />}
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Inspection'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/quality-control')}
                disabled={loading}
              >
                Cancel
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default QualityControlCreate;